import {
  ReactNode,
  memo,
  useState,
  useMemo,
  useCallback,
  PropsWithChildren
} from 'react';
import { createPortal } from 'react-dom';
import { CircleArrowRight, CircleX } from 'lucide-react';

// Re-resizable
import { Resizable } from 're-resizable';

import { cn } from '@/shadcn/lib/utils';
import { Button } from '@/shadcn/button';

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/shadcn/tooltip';
import ReactMarkdown from 'react-markdown';

type Props = PropsWithChildren<{
  portalEl: HTMLElement | null; // the container where the tray will be teleported
  title?: ReactNode;
  isLeftSide?: boolean; // whether the tray opens from the left side of the screen
  width?: number; // width of the tray when uncollapsed
  collapsedWidth?: number; // width of the tray when collapsed
  resizable?: boolean; // whether the tray is resizable when uncollapsed
  minWidth?: number; // minimun width if the tray is resizable,
  onResize?: (newWidth: number) => void; // callback when resizing stops (if something in the caller needs to be updated)
  onClose?: () => void; // callback when clicking on the cross icon when collapsed (not displayed if unset prop)
}>;

export const Tray = memo(
  ({
    portalEl,
    title,
    children,
    isLeftSide = false,
    width = 450,
    collapsedWidth = 54,
    resizable = true,
    minWidth = 200,
    onResize,
    onClose
  }: Props) => {
    const [collapsed, setCollapsed] = useState(false);

    const [currentWidth, setCurrentWidth] = useState(width);
    const [isResizing, setIsResizing] = useState(false);
    const [resizingWidth, setResizingWidth] = useState(width);

    // work out the current tray width
    const isResizable = resizable && !collapsed;
    let usedWidth = collapsed ? collapsedWidth : currentWidth;
    if (isResizing) {
      usedWidth = resizingWidth;
    }

    const trayEnable = useMemo(
      () => ({
        top: false,
        right: isResizable && isLeftSide,
        bottom: false,
        left: isResizable && !isLeftSide,
        topRight: false,
        bottomRight: false,
        bottomLeft: false,
        topLeft: false
      }),
      [isResizable, isLeftSide]
    );

    const onResizeStart = useCallback(() => setIsResizing(true), []);
    const onResizeInternal = useCallback(
      (...args) => {
        const d = args[3];
        setResizingWidth(currentWidth + d.width);
      },
      [currentWidth]
    );
    const onResizeStop = useCallback(() => {
      setCurrentWidth(resizingWidth);
      setIsResizing(false);
      if (onResize) onResize(resizingWidth);
    }, [resizingWidth, onResize]);

    const trayContent = useMemo(() => {
      return (
        <>
          <div
            className={cn(
              'border-border flex h-[70px] w-full shrink-0 flex-row-reverse flex-nowrap items-center justify-between border-b p-2.5',
              collapsed && 'border-b-0',
              isLeftSide && 'flex-row flex-nowrap'
            )}
            data-is-collapsed={collapsed}
            data-is-inverted={isLeftSide}
          >
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="largeIcon"
                    data-is-collapsed={collapsed}
                    data-is-inverted={isLeftSide}
                    onClick={() => setCollapsed(!collapsed)}
                    data-cy="collapse-toggle"
                    aria-label="Click to toggle the tray"
                    className="text-muted-foreground"
                  >
                    <CircleArrowRight
                      className={cn(
                        'transition-transform duration-150 ease-out',
                        collapsed && 'rotate-180',
                        isLeftSide && 'rotate-180',
                        isLeftSide && collapsed && 'rotate-0'
                      )}
                    />
                  </Button>
                </TooltipTrigger>

                <TooltipContent side={isLeftSide ? 'right' : 'left'}>
                  {collapsed
                    ? 'Click to expand the tray'
                    : 'Click to collapse the tray'}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>

            {!collapsed && (
              <ReactMarkdown className="mr-2.5 truncate text-lg font-medium">
                {title as string}
              </ReactMarkdown>
            )}
          </div>
          {collapsed && (
            <div className="-mt-2 flex grow flex-col flex-nowrap items-center overflow-hidden">
              <div
                className="truncate text-lg font-medium"
                style={{
                  writingMode: 'vertical-lr'
                }}
              >
                {title}
              </div>
            </div>
          )}
          {collapsed && onClose && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="largeIcon"
                    onClick={onClose}
                    data-cy="close-tray"
                    aria-label="Click to close the tray"
                    className="text-muted-foreground mx-auto mb-2"
                  >
                    <CircleX />
                  </Button>
                </TooltipTrigger>

                <TooltipContent side={isLeftSide ? 'right' : 'left'}>
                  <p>Click to close the tray</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          {/* note: always render the body so as not to loose the content when collapsed */}
          <div
            className={cn(
              'flex grow flex-col flex-nowrap overflow-hidden',
              collapsed && 'invisible h-0 grow-0'
            )}
            data-is-hidden={collapsed}
          >
            {children}
          </div>
        </>
      );
    }, [collapsed, isLeftSide, title, onClose, children]);

    const resizableStyle = useMemo(
      () => ({ transition: isResizing ? 'none' : 'width .25s' }),
      [isResizing]
    );
    const resizableSize = useMemo(
      () => ({
        width: usedWidth,
        height: '100%'
      }),
      [usedWidth]
    );

    return createPortal(
      <Resizable
        className="bg-card border-border flex flex-col flex-nowrap rounded-lg border shadow"
        handleWrapperClass="z-5"
        style={resizableStyle}
        minWidth={isResizing ? minWidth : 0}
        size={resizableSize}
        enable={trayEnable}
        onResizeStart={onResizeStart}
        onResize={onResizeInternal}
        onResizeStop={onResizeStop}
        data-cy="tray"
      >
        {trayContent}
      </Resizable>,
      portalEl || document.body
    );
  }
);
